import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import PageLayout from '../../layouts/PageLayout';
import { shiftTemplateAPI } from '../../services';
import { toast } from 'react-toastify';
import { useBranch } from '../../hooks';
import './ShiftRevenueRankingPage.css';

/**
 * Bảng ca theo doanh thu để QUẢN LÝ TỰ GẮN CỜ "ca đông" (#130536).
 *
 * #220322 (lệnh sếp update_id=731599712) — sếp bác bản trước vì tách dòng nhưng cờ vẫn dùng chung:
 * *"tôi nói tách ca riêng ví dụ như ca 16.00 thì tách thành bảy ca 16.00 riêng và gắn cờ riêng
 * doanh thu riêng, chứ gắn cờ vẫn gắn cờ chung là, ca 16.00 thứ bảy thì doanh thu cao hơn những
 * ngày trong tuần khác biệt nhau chứ đâu thể nào mà gắn cờ giống nhau ... tách riêng thành đủ số
 * lượng ca theo tuần cho tôi rồi xếp hạng doanh thu từ cao xuống thấp, trung bình của bốn tuần
 * gần nhất"*.
 *
 * Nên màn này nay là:
 *  1. MỘT DÒNG = MỘT (ca mẫu × thứ), mỗi ca mẫu ra ĐỦ 7 dòng (kể cả thứ chưa xếp ca — sếp cần chỗ
 *     gắn cờ trước).
 *  2. MỖI DÒNG CÓ CÔNG TẮC CỜ RIÊNG. Bật Thứ Bảy không đụng Thứ Hai.
 *  3. Doanh thu mỗi dòng = trung bình 4 lần gần nhất CỦA CHÍNH THỨ ĐÓ (máy chủ tính, kèm danh
 *     sách đúng các ngày đã lấy để bấm ra xem đối chiếu).
 *  4. MẶC ĐỊNH XẾP HẠNG doanh thu cao → thấp trên toàn bộ danh sách.
 */

const DAY_LABELS = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
const DAY_FULL = [
  'Chủ nhật', 'Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy',
];

/** Thứ tự đọc quen của người Việt: T2 → CN. */
const vnDayOrder = (dow) => (dow == null ? 99 : (dow + 6) % 7);

/** Dưới ngần này lượt thì trung bình chưa đáng tin (khớp `MIN_RELIABLE_SLOT_SAMPLES` máy chủ). */
const MIN_RELIABLE_SAMPLES = 3;

const formatCurrency = (value) =>
  new Intl.NumberFormat('vi-VN').format(Math.round(Number(value || 0))) + 'đ';

const formatMinutes = (minutes) => {
  const total = Math.round(Number(minutes || 0));
  if (total <= 0) return '0 phút';
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m} phút`;
  if (m === 0) return `${h} giờ`;
  return `${h}g${String(m).padStart(2, '0')}`;
};

/** yyyy-MM-dd → dd/MM (đọc nhanh trên điện thoại). */
const formatShortDate = (ymd) => {
  const parts = String(ymd || '').split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}` : ymd;
};

const WORKLOAD_BADGE = {
  LIGHT: { bg: 'success', text: 'Nhẹ' },
  MEDIUM: { bg: 'warning', text: 'Vừa' },
  HEAVY: { bg: 'danger', text: 'Nặng' },
  UNKNOWN: { bg: 'secondary', text: 'Chưa đo được' },
};

const LOOKBACK_OPTIONS = [2, 4, 8, 12, 26];

const dayLabel = (day) => (day == null ? '—' : DAY_LABELS[day] ?? day);
const dayFull = (day) => (day == null ? '—' : DAY_FULL[day] ?? day);
const rowKey = (row) => row.row_key || `${row.template_id}#${row.day_of_week}`;

/** Câu giải thích số "thời gian xong hết việc" đến từ đâu. */
const workloadSourceNote = (wl) => {
  if (wl.estimate_source === 'SURVEYED') {
    return (
      `nhân viên khảo sát thật ${wl.surveyed_task_count} việc` +
      (wl.surveyed_fallback_count > 0
        ? ` · ${wl.surveyed_fallback_count} việc còn dùng ước lượng khai tay`
        : '')
    );
  }
  if (wl.estimate_source === 'MEASURED') {
    return wl.measured_scope === 'TEMPLATE'
      ? `thứ này chưa đủ lượt để đo riêng — đang lấy số đo chung của cả ca (${wl.measured_slots} lượt)`
      : `đo thật trên ${wl.measured_slots} lượt ca của đúng thứ này`;
  }
  if (wl.estimate_source === 'DECLARED') {
    return 'theo thời lượng khai trong Cấu hình Task (chưa đủ lịch sử để đo)';
  }
  return 'chưa có dữ liệu để kết luận';
};

/** Cảnh báo mẫu mỏng, nói bằng lời thường. */
const ThinSampleNote = ({ row }) => {
  const wl = row.workload || {};
  const notes = [];
  if (row.slot_samples === 0) {
    notes.push('Thứ này chưa có lượt ca nào trong kỳ đang chọn — chưa có gì để tính.');
  } else if (row.low_sample) {
    notes.push(
      `Mới ${row.slot_samples}/${row.weeks_requested} tuần có ca — trung bình chưa chắc chắn.`,
    );
  }
  if (
    wl.estimate_source === 'MEASURED' &&
    wl.measured_scope === 'DAY' &&
    wl.measured_slots > 0 &&
    wl.measured_slots < MIN_RELIABLE_SAMPLES &&
    wl.measured_slots !== row.slot_samples
  ) {
    notes.push(`Thời gian chỉ đo được trên ${wl.measured_slots} lượt.`);
  }
  if (notes.length === 0) return null;
  return (
    <div className="srr-thin-note" data-testid={`thin-${rowKey(row)}`}>
      ⚠️ {notes.join(' ')}
    </div>
  );
};

/**
 * Phép tính doanh thu của dòng, bày ra đúng các ngày đã lấy.
 * Đây là chỗ sếp kiểm được: cộng mấy số này chia số ngày ra đúng con số hiển thị.
 */
const RevenueBreakdown = ({ row }) => {
  const dates = row.revenue_dates || [];
  if (dates.length === 0) return null;
  return (
    <div className="srr-breakdown" data-testid={`breakdown-${rowKey(row)}`}>
      {dates.map((d) => (
        <span className="srr-chip-date" key={d.date}>
          {formatShortDate(d.date)}: {formatCurrency(d.revenue)}
        </span>
      ))}
      <div className="srr-breakdown-sum">
        Cộng {dates.length} lần {dayFull(row.day_of_week)} gần nhất rồi chia {dates.length} ={' '}
        <b>{formatCurrency(row.avg_revenue)}</b>
      </div>
    </div>
  );
};

/** Khối "thời gian xong hết việc phải nghiệm thu" của MỘT dòng. */
const WorkloadCell = ({ row }) => {
  const wl = row.workload || {};
  const badge = WORKLOAD_BADGE[wl.level] || WORKLOAD_BADGE.UNKNOWN;
  return (
    <>
      <Badge bg={badge.bg}>{badge.text}</Badge>{' '}
      <span className="fw-semibold">
        {wl.estimate_source === 'NONE' ? '—' : formatMinutes(wl.estimated_minutes)}
      </span>{' '}
      <span className="text-muted">({wl.percent_of_shift || 0}% độ dài ca)</span>
      <div className="text-muted srr-small">{workloadSourceNote(wl)}</div>
      {wl.estimate_source === 'SURVEYED' && wl.measured_slots > 0 && (
        <div className="text-muted srr-small">
          đo thật cả cụm: {formatMinutes(wl.measured_minutes)} trên {wl.measured_slots} lượt ca
          {wl.measured_scope === 'TEMPLATE' ? ' (số chung cả ca)' : ''}
        </div>
      )}
      {/* Đã có khảo sát nhưng CHƯA phủ hết việc trong ca ⇒ chưa thay số chính (#142431). */}
      {wl.estimate_source !== 'SURVEYED' && wl.surveyed_task_count > 0 && (
        <div className="text-muted srr-small">
          nhân viên đã khảo sát {wl.surveyed_task_count}/{wl.task_count} việc — còn{' '}
          {(wl.task_count || 0) -
            (wl.surveyed_task_count || 0) -
            (wl.surveyed_fallback_count || 0)}{' '}
          việc chưa có số nên chưa thay được số chính
        </div>
      )}
    </>
  );
};

export default function ShiftRevenueRankingPage() {
  const { selectedBranch } = useBranch();
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const [board, setBoard] = useState(null);
  const [lookbackWeeks, setLookbackWeeks] = useState(4);
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const [hideEmptyDays, setHideEmptyDays] = useState(false);
  // Mặc định XẾP HẠNG (đúng lời sếp); đổi sang 'CA' khi muốn xem gom theo ca mẫu.
  const [groupBy, setGroupBy] = useState('HANG');
  const [openBreakdown, setOpenBreakdown] = useState({});

  const branchId = selectedBranch?.id || null;

  const loadBoard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await shiftTemplateAPI.getRevenueRanking({
        ...(branchId ? { branch_id: branchId } : {}),
        lookback_weeks: lookbackWeeks,
      });
      setBoard(res?.data || res || null);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Không tải được bảng ca theo doanh thu',
      );
      setBoard(null);
    } finally {
      setLoading(false);
    }
  }, [branchId, lookbackWeeks]);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  const rows = useMemo(() => {
    const all = board?.rows || [];
    const visible = all.filter((r) => {
      if (onlyFlagged && !r.is_high_revenue) return false;
      if (hideEmptyDays && !r.slot_samples) return false;
      return true;
    });
    if (groupBy !== 'CA') return visible;
    // Xem gom theo ca mẫu: giữ nguyên thứ hạng giữa các CA (ca có doanh thu cao nhất lên trước),
    // trong mỗi ca xếp T2 → CN.
    const order = [];
    const byTemplate = new Map();
    for (const row of visible) {
      if (!byTemplate.has(row.template_id)) {
        byTemplate.set(row.template_id, []);
        order.push(row.template_id);
      }
      byTemplate.get(row.template_id).push(row);
    }
    return order.flatMap((id) =>
      byTemplate
        .get(id)
        .slice()
        .sort((a, b) => vnDayOrder(a.day_of_week) - vnDayOrder(b.day_of_week)),
    );
  }, [board, onlyFlagged, hideEmptyDays, groupBy]);

  /** Số (ca × thứ) đang gắn cờ — đơn vị đếm nay là DÒNG, không phải ca mẫu. */
  const flaggedCount = useMemo(
    () => (board?.rows || []).filter((r) => r.is_high_revenue).length,
    [board],
  );

  /**
   * #220322 — bật/tắt cờ cho ĐÚNG (ca mẫu × thứ) của dòng này.
   * Cập nhật tại chỗ CHỈ dòng đó — cố ý không đụng dòng khác cùng ca mẫu, vì đó đúng là thứ sếp
   * phàn nàn ở bản trước.
   */
  const toggleFlag = async (row) => {
    const next = !row.is_high_revenue;
    const key = rowKey(row);
    setSavingKey(key);
    try {
      await shiftTemplateAPI.setHighRevenueForDay(
        row.template_id,
        row.day_of_week,
        next,
      );
      setBoard((prev) =>
        prev
          ? {
              ...prev,
              rows: prev.rows.map((r) => {
                if (rowKey(r) === key) return { ...r, is_high_revenue: next };
                // Bộ đếm "cả ca có mấy thứ bật" phải theo kịp, nhưng CỜ của dòng khác giữ nguyên.
                if (r.template_id === row.template_id) {
                  return {
                    ...r,
                    template_flagged_day_count:
                      (r.template_flagged_day_count || 0) + (next ? 1 : -1),
                  };
                }
                return r;
              }),
            }
          : prev,
      );
      toast.success(
        next
          ? `Đã gắn cờ ca đông: ${row.name} — ${dayFull(row.day_of_week)}`
          : `Đã bỏ cờ ca đông: ${row.name} — ${dayFull(row.day_of_week)}`,
      );
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Không đổi được cờ ca đông');
    } finally {
      setSavingKey(null);
    }
  };

  const toggleBreakdown = (key) =>
    setOpenBreakdown((prev) => ({ ...prev, [key]: !prev[key] }));

  /** Công tắc cờ của MỘT dòng (ca × thứ). */
  const renderFlagSwitch = (row) => (
    <Form.Check
      type="switch"
      className="srr-flag"
      id={`flag-${rowKey(row)}`}
      data-testid={`flag-${rowKey(row)}`}
      checked={!!row.is_high_revenue}
      disabled={savingKey === rowKey(row)}
      onChange={() => toggleFlag(row)}
      label={row.is_high_revenue ? 'Ca đông' : 'Đánh dấu ca đông'}
    />
  );

  return (
    <PageLayout>
      <Row className="mb-3">
        <Col>
          <h4 className="mb-1">Ca theo doanh thu — gắn cờ &quot;ca đông&quot;</h4>
          <div className="text-muted srr-small">
            Mỗi ca mẫu được tách thành <b>7 ca riêng theo 7 thứ</b>, mỗi dòng có{' '}
            <b>doanh thu riêng và cờ riêng</b>. Doanh thu = trung bình{' '}
            <b>{lookbackWeeks} lần gần nhất của chính thứ đó</b> (bấm vào con số để xem đúng những
            ngày đã lấy). Danh sách xếp <b>doanh thu cao xuống thấp</b>. Bot đề xuất ca sẽ KHÔNG tự
            đề xuất nhân viên mới vào đúng những (ca × thứ) được đánh dấu.
          </div>
        </Col>
      </Row>

      <Card className="mb-3">
        <Card.Body>
          <Row className="align-items-end g-3">
            <Col xs={12} md={3}>
              <Form.Label className="mb-1">Kỳ tính doanh thu</Form.Label>
              <Form.Select
                value={lookbackWeeks}
                onChange={(e) => setLookbackWeeks(Number(e.target.value))}
                data-testid="lookback-select"
              >
                {LOOKBACK_OPTIONS.map((w) => (
                  <option key={w} value={w}>
                    {w} tuần gần nhất
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} md={3}>
              <Form.Label className="mb-1">Cách xếp</Form.Label>
              <Form.Select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
                data-testid="groupby-select"
              >
                <option value="HANG">Xếp hạng doanh thu cao → thấp</option>
                <option value="CA">Gom theo ca (trong ca xếp T2 → CN)</option>
              </Form.Select>
            </Col>
            <Col xs={12} md={3}>
              <Form.Check
                type="switch"
                id="only-flagged"
                label={`Chỉ hiện dòng đang gắn cờ (${flaggedCount})`}
                checked={onlyFlagged}
                onChange={(e) => setOnlyFlagged(e.target.checked)}
              />
              <Form.Check
                type="switch"
                id="hide-empty-days"
                data-testid="hide-empty-days"
                label="Ẩn thứ chưa có lượt ca nào"
                checked={hideEmptyDays}
                onChange={(e) => setHideEmptyDays(e.target.checked)}
              />
            </Col>
            <Col xs={12} md={3} className="text-md-end">
              <Button variant="outline-secondary" onClick={loadBoard} disabled={loading}>
                Tải lại
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {!branchId && !loading && (
        <Alert variant="info" className="py-2">
          Đang xem TẤT CẢ chi nhánh — thứ hạng doanh thu xếp chung. Chọn chi nhánh ở thanh
          trên để xem riêng từng nơi.
        </Alert>
      )}

      {loading && (
        <div className="text-center py-4">
          <Spinner animation="border" />
        </div>
      )}

      {!loading && board && rows.length === 0 && (
        <Alert variant="secondary">
          {onlyFlagged
            ? 'Chưa có (ca × thứ) nào được gắn cờ ca đông.'
            : hideEmptyDays
              ? 'Không có thứ nào có lượt ca trong kỳ đang chọn.'
              : 'Chưa có ca nào đang hoạt động.'}
        </Alert>
      )}

      {!loading && rows.length > 0 && (
        <>
          <div className="text-muted srr-small mb-2" data-testid="row-count">
            {rows.length} dòng (ca × thứ) · {flaggedCount} dòng đang gắn cờ ca đông
          </div>

          {/*
            ĐIỆN THOẠI TRƯỚC (luật #731599378): mỗi ca đẻ 7 dòng nên bảng dài gấp 7 lần; bảng
            nhiều cột không đọc nổi ở 390px mà không cuộn ngang — trong khi cột "Thứ" và công tắc
            cờ mới là thứ sếp cần. Khổ điện thoại dùng THẺ, mỗi dòng một thẻ.
          */}
          <div className="d-lg-none">
            {rows.map((row) => (
              <Card
                className={`mb-2 srr-card${row.is_high_revenue ? ' srr-card-flagged' : ''}`}
                key={rowKey(row)}
                data-testid={`row-${rowKey(row)}`}
              >
                <Card.Body className="p-3">
                  <div className="srr-day-top">
                    <span className="srr-rank">{row.rank}</span>
                    <span className="srr-day-chip">{dayLabel(row.day_of_week)}</span>
                    <span className="fw-semibold">{row.name}</span>
                  </div>
                  <div className="text-muted srr-small">
                    {row.time_range} · {row.shift_kind === 'MAIN' ? 'Ca chính' : 'Ca gãy'}
                    {row.is_qc ? ' · QC' : ''} · tối đa {row.max_employees} NV
                    {row.branch_name ? ` · ${row.branch_name}` : ''}
                  </div>

                  <div className="srr-money-line">
                    <button
                      type="button"
                      className="srr-money-btn"
                      data-testid={`money-${rowKey(row)}`}
                      onClick={() => toggleBreakdown(rowKey(row))}
                    >
                      {row.slot_samples > 0 ? formatCurrency(row.avg_revenue) : '— chưa có ca'}
                      {row.revenue_dates?.length > 0 && (
                        <span className="srr-money-caret">
                          {openBreakdown[rowKey(row)] ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                    <span className="text-muted srr-small">
                      {row.slot_samples}/{row.weeks_requested} tuần có ca
                    </span>
                  </div>
                  {openBreakdown[rowKey(row)] && <RevenueBreakdown row={row} />}
                  <ThinSampleNote row={row} />

                  {row.slot_samples > 0 && (
                    <div className="text-muted srr-small">
                      Đơn TB {row.avg_orders_per_slot} · NV TB {row.avg_employees_per_slot}
                      {(row.top_employees || []).length > 0 && (
                        <>
                          {' · '}
                          {row.top_employees
                            .map((e) => `${e.full_name || e.employee_id} (${e.slots})`)
                            .join(', ')}
                        </>
                      )}
                    </div>
                  )}
                  <div className="srr-small mt-1">
                    <WorkloadCell row={row} />
                  </div>
                  <div className="srr-flag-line">{renderFlagSwitch(row)}</div>
                </Card.Body>
              </Card>
            ))}
          </div>

          {/* Khổ máy tính: bảng nhiều cột, vẫn một dòng = một (ca × thứ). */}
          <Card className="d-none d-lg-block">
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0 align-middle" size="sm">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 50 }}>#</th>
                    <th style={{ width: 60 }}>Thứ</th>
                    <th>Ca</th>
                    <th className="text-end">Doanh thu TB / ca</th>
                    <th className="text-end">Số tuần có ca</th>
                    <th className="text-end">Đơn TB</th>
                    <th className="text-end">NV TB</th>
                    <th>Ai trực</th>
                    <th>Thời gian xong hết việc phải nghiệm thu</th>
                    <th style={{ width: 150 }}>Ca đông</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={rowKey(row)}
                      className={row.is_high_revenue ? 'table-warning' : undefined}
                      data-testid={`row-${rowKey(row)}`}
                    >
                      <td>{row.rank}</td>
                      <td className="fw-semibold">{dayLabel(row.day_of_week)}</td>
                      <td>
                        <div className="fw-semibold">{row.name}</div>
                        <div className="text-muted srr-small">
                          {row.time_range} ·{' '}
                          {row.shift_kind === 'MAIN' ? 'Ca chính' : 'Ca gãy'}
                          {row.is_qc ? ' · QC' : ''} · tối đa {row.max_employees} NV
                        </div>
                        <div className="text-muted srr-small">{row.branch_name}</div>
                      </td>
                      <td className="text-end fw-semibold">
                        <button
                          type="button"
                          className="srr-money-btn"
                          data-testid={`money-${rowKey(row)}`}
                          onClick={() => toggleBreakdown(rowKey(row))}
                        >
                          {row.slot_samples > 0
                            ? formatCurrency(row.avg_revenue)
                            : '— chưa có ca'}
                          {row.revenue_dates?.length > 0 && (
                            <span className="srr-money-caret">
                              {openBreakdown[rowKey(row)] ? '▲' : '▼'}
                            </span>
                          )}
                        </button>
                        {openBreakdown[rowKey(row)] && <RevenueBreakdown row={row} />}
                      </td>
                      <td className="text-end">
                        {row.slot_samples}/{row.weeks_requested}
                        {row.low_sample && (
                          <span className="srr-thin-flag" title="mẫu quá ít">
                            {' '}
                            ⚠️
                          </span>
                        )}
                      </td>
                      <td className="text-end">{row.avg_orders_per_slot}</td>
                      <td className="text-end">{row.avg_employees_per_slot}</td>
                      <td className="srr-small" style={{ maxWidth: 180 }}>
                        {(row.top_employees || []).length === 0
                          ? '—'
                          : row.top_employees
                              .map((e) => `${e.full_name || e.employee_id} (${e.slots})`)
                              .join(', ')}
                      </td>
                      <td className="srr-small" style={{ minWidth: 230 }}>
                        <WorkloadCell row={row} />
                        <ThinSampleNote row={row} />
                      </td>
                      <td>{renderFlagSwitch(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </>
      )}

      {!loading && rows.length > 0 && (
        <div className="text-muted mt-2 srr-small">
          <b>Cách tính doanh thu mỗi dòng:</b> lấy {lookbackWeeks} lần gần nhất của ĐÚNG thứ đó
          (ví dụ 4 ngày Thứ Bảy gần nhất của ca 16:00), cộng doanh thu lại rồi chia cho số lần
          thực có — bấm vào con số để xem đúng những ngày đã lấy. Thứ chưa đủ {lookbackWeeks} lần
          thì chia cho số lần thực có chứ không chia cứng, và có ghi rõ &quot;mấy/{lookbackWeeks}{' '}
          tuần có ca&quot;. Ca gãy/ca đêm tính theo <b>ngày BẮT ĐẦU ca</b>, theo giờ Việt Nam.
          <br />
          <b>Cột thời gian:</b> khi thứ đó đã có lịch sử, số hiển thị là TRUNG VỊ khoảng từ giờ bắt
          đầu ca tới lúc việc phải nghiệm thu cuối cùng được bấm xong; thứ chưa đủ lượt để đo riêng
          thì mượn số đo chung của cả ca và có ghi chú rõ. Đây là khoảng cách giữa các lần BẤM NÚT,
          nên nhân viên để dồn cuối ca mới bấm sẽ làm ca đó trông nặng hơn thực tế — dùng để so
          sánh ca nào nặng hơn ca nào, không dùng để chấm công.
        </div>
      )}
    </PageLayout>
  );
}
